/**
 * Agent 管理器
 * 负责 Agent Workflow 模块的业务逻辑
 * @author Joey
 */

import { useEffect, useState } from "react";
import { v4 as uuid } from 'uuid';
import { useTranslation } from "react-i18next";
import { useParams } from 'react-router-dom';
import { useToast } from "@/hooks/use-toast";
import { agentDetail } from "@/services/agent-detail-id";
import { agentSetSubscription } from "@/services/agent-set-subscription";
import { agentSetWorkflow } from "@/services/agent-set-workflow";
import { canUseWindow } from "@/utils/canuseWindow";
import deepEqual from "@/utils/deep-equal";
import { useRouteNavigateHook } from "./route-navigate-manager";
import { userManagementManager } from "./agent-dashboard-user-management-manager";

interface AgentSetWorkflow {
  workflow: string;
  workflowAddress: string;
}

interface AgentSubscription {
  subscriptionTime: number;
  subscriptionFrequency: number;
}

type workflowChangeEventDetail = {
  agentWorkflow: AgentSetWorkflow;
};
type workflowChangeEvent = CustomEvent<workflowChangeEventDetail>;

type subscriptionChangeEventDetail = {
  agentSubscription: AgentSubscription;
};
type subscriptionChangeEvent = CustomEvent<subscriptionChangeEventDetail>;

export class AgentWorkflowSubscriptionManager {
  private static instance: AgentWorkflowSubscriptionManager;
  private readonly STORAGE_KEY = "MIUTA_AGENT_DETAIL";
  private agentWorkflow: AgentSetWorkflow = {} as AgentSetWorkflow;
  private originAgentWorkflow: AgentSetWorkflow = {} as AgentSetWorkflow;

  private agentSubscription: AgentSubscription = {} as AgentSubscription;
  private originAgentSubscription: AgentSubscription = {} as AgentSubscription;

  private agentId: string = "";

  private constructor() { }

  // 单例模式
  public static getInstance(): AgentWorkflowSubscriptionManager {
    if (!AgentWorkflowSubscriptionManager.instance) {
      AgentWorkflowSubscriptionManager.instance = new AgentWorkflowSubscriptionManager();
    }
    return AgentWorkflowSubscriptionManager.instance;
  }

  async init(agentId: string) {
    this.agentId = agentId;
    // 如果没有数据,才初始化
    if (Object.keys(this.agentWorkflow).length > 0) {
      return;
    }
    // 初始化时先清空数据，避免旧数据干扰
    this.clearWorkflowAndSubscription();
    await this.fetchWorkflow();
  }

  /** 请求 workflow 数据 */
  async fetchWorkflow() {
    try {
      const data = await agentDetail(this.getAgentId());
      this.setAgentWorkflow({
        workflow: data.workflow || "",
        workflowAddress: data.workflowAddress || "",
      });
      this.setOriginAgentWorkflow(this.getAgentWorkflow());

      this.setAgentSubscription({
        subscriptionTime: data.subscriptionTime,
        subscriptionFrequency: data.subscriptionFrequency,
      });
      this.setOriginAgentSubscription(this.getAgentSubscription());
    } catch (error) {
      console.error("Failed to fetch agent workflow:", error);
      throw error;
    }
  }

  /** 更新 workflow 数据 */
  async updateWorkflow() {
    try {
      await agentSetWorkflow({
        agentId: this.getAgentId(),
        ...this.getAgentWorkflow(),
      });
    } catch (error) {
      console.error("Failed to update agent workflow:", error);
      throw error;
    }
  }

  /** 更新 subscription 数据 */
  async updateSubscription() {
    try {
      await agentSetSubscription({
        type: 1,
        agentId: this.getAgentId(),
        ...this.getAgentSubscription(),
      });
    } catch (error) {
      console.error("Failed to update agent subscription:", error);
      throw error;
    }
  }

  /** 清除 workflow 和 subscription 数据 */
  async clearWorkflowAndSubscription() {
    this.setAgentWorkflow({} as AgentSetWorkflow);
    this.setAgentSubscription({} as AgentSubscription);
  }

  // ==================== 读取写入操作 ====================
  public getAgentWorkflow() {
    return this.agentWorkflow;
  }
  public setAgentWorkflow(agentWorkflow: AgentSetWorkflow) {
    this.agentWorkflow = agentWorkflow;
    this.dispatchAgentWorkflowChange();
  }

  public getOriginAgentWorkflow() {
    return this.originAgentWorkflow;
  }
  public setOriginAgentWorkflow(originAgentWorkflow: AgentSetWorkflow) {
    this.originAgentWorkflow = originAgentWorkflow;
  }

  public getAgentSubscription() {
    return this.agentSubscription;
  }
  public setAgentSubscription(agentSubscription: AgentSubscription) {
    this.agentSubscription = agentSubscription;
    this.dispatchAgentSubscriptionChange();
  }

  public getOriginAgentSubscription() {
    return this.originAgentSubscription;
  }
  public setOriginAgentSubscription(originAgentSubscription: AgentSubscription) {
    this.originAgentSubscription = originAgentSubscription;
  }

  public getAgentId(): string {
    return this.agentId;
  }
  public setAgentId(agentId: string) {
    this.agentId = agentId;
  }

  // ==================== 订阅通知操作 ====================
  // 监听 workflow 变化
  public onAgentWorkflowChange(listener: (e: workflowChangeEvent) => void) {
    if (canUseWindow()) {
      const eventName = `${this.STORAGE_KEY}:workflow-change`;
      window.addEventListener(eventName, listener as EventListener);

      return () => {
        window.removeEventListener(eventName, listener as EventListener);
      };
    }
    return () => { };
  }
  public dispatchAgentWorkflowChange(): void {
    if (canUseWindow()) {
      const event = new CustomEvent<workflowChangeEventDetail>(
        `${this.STORAGE_KEY}:workflow-change`,
        {
          detail: {
            agentWorkflow: this.agentWorkflow,
          },
        }
      );
      window.dispatchEvent(event);
    }
  }

  // 监听 subscription 变化
  public onAgentSubscriptionChange(listener: (e: subscriptionChangeEvent) => void) {
    if (canUseWindow()) {
      const eventName = `${this.STORAGE_KEY}:subscription-change`;
      window.addEventListener(eventName, listener as EventListener);

      return () => {
        window.removeEventListener(eventName, listener as EventListener);
      };
    }
    return () => { };
  }
  public dispatchAgentSubscriptionChange(): void {
    if (canUseWindow()) {
      const event = new CustomEvent<subscriptionChangeEventDetail>(
        `${this.STORAGE_KEY}:subscription-change`,
        {
          detail: {
            agentSubscription: this.agentSubscription,
          },
        }
      );
      window.dispatchEvent(event);
    }
  }
}

// ============= Hooks =============
const useAgentWorkflowSubscriptionData = () => {
  const toast = useToast();
  const params = useParams();

  const [agentWorkflow, setAgentWorkflow] = useState(agentWorkflowSubscriptionManager.getAgentWorkflow());
  const [agentSubscription, setAgentSubscription] = useState(agentWorkflowSubscriptionManager.getAgentSubscription());
  const [loading, setLoading] = useState(false);

  const init = async () => {
    setLoading(true);
    try {
      await agentWorkflowSubscriptionManager.init(params.id || "");
    } catch (error) {
      toast.showToastByCode(error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    init();
  }, []);

  useEffect(() => {
    return agentWorkflowSubscriptionManager.onAgentWorkflowChange((e) => {
      setAgentWorkflow(e.detail.agentWorkflow);
    });
  }, []);

  useEffect(() => {
    return agentWorkflowSubscriptionManager.onAgentSubscriptionChange((e) => {
      setAgentSubscription(e.detail.agentSubscription);
    });
  }, []);

  return {
    agentWorkflow,
    agentSubscription,
    loading,
  }
}

/** 监听页面路由跳转前动作 */
const useListenerBeforeAction = (page: 'workflow' | 'subscription' = 'workflow') => {
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  // 新增状态：标记弹窗是否由路由跳转拦截触发
  const [isTriggeredByRouter, setIsTriggeredByRouter] = useState(false);

  const { isChanged: isWorkflowChanged } = useAgentWorkflowChanged();
  const { isChanged: isSubscriptionChanged } = useAgentSubscriptionChanged();

  const { beforeListener, afterListener } = useRouteNavigateHook.useRouterListener();

  const isChanged = page === 'workflow' ? isWorkflowChanged : isSubscriptionChanged

  const listenerPath = [
    '/agent/dashboard/user-management/',
    '/agent/dashboard/workflow/',
    '/agent/dashboard/subscription-settings/'
  ];

  // 监听路由跳转
  const { next, removeBeforeListener } = beforeListener(`before-${page}-${uuid()}`, (from, to) => {
    // 只有跳转至用户管理、工作流、订阅设置页面才需要确认
    if (listenerPath.some(path => to.pathname.includes(path)) && isChanged) {
      // 路由拦截触发弹窗，标记来源
      setIsTriggeredByRouter(true);
      setShowConfirmModal(true);
      return false;
    }
    return true;
  });

  // 用户主动点击取消，触发弹窗（标记为非路由拦截）
  const triggerManualConfirm = () => {
    // 标记为用户主动触发
    setIsTriggeredByRouter(false);
    setShowConfirmModal(true);
  };

  useEffect(() => {
    // 组件卸载时取消监听
    return () => {
      removeBeforeListener();
    };
  }, [beforeListener, afterListener]);

  return {
    showConfirmModal,
    setShowConfirmModal,
    isTriggeredByRouter,
    triggerManualConfirm,
    next
  }
}

/** 监听页面路由跳转后动作 */
const useListenerAfterAction = (page: 'workflow' | 'subscription' = 'workflow') => {
  const { afterListener } = useRouteNavigateHook.useRouterListener();
  const listenerPath = [
    '/agent/dashboard/user-management/',
    '/agent/dashboard/workflow/',
    '/agent/dashboard/subscription-settings/'
  ];

  useEffect(() => {
    const id = `after-${page}-after`;
    const { removeAfterListener } = afterListener(id, async (from, to) => {
      if (!listenerPath.some((path) => to.pathname.includes(path))) {
        console.log('清除用户列表, Agent 工作流、订阅数据');
        agentWorkflowSubscriptionManager.clearWorkflowAndSubscription();
        userManagementManager.resetAgentUsersListAndSearchForm()
      }
    });

    return () => {
      removeAfterListener();
    };
  }, []);
}

/** 操作 agent workflow、subscription 数据 */
const useAgentAction = () => {
  // 重置 workflow 数据
  const resetAgentWorkflow = () => {
    agentWorkflowSubscriptionManager.setAgentWorkflow(agentWorkflowSubscriptionManager.getOriginAgentWorkflow());
  }

  // 重置 subscription 数据
  const resetAgentSubscription = () => {
    agentWorkflowSubscriptionManager.setAgentSubscription(agentWorkflowSubscriptionManager.getOriginAgentSubscription());
  }

  // 清空 workflow 数据
  const clearAgentWorkflow = () => {
    agentWorkflowSubscriptionManager.setAgentWorkflow({} as AgentSetWorkflow);
  }

  // 清空 subscription 数据
  const clearAgentSubscription = () => {
    agentWorkflowSubscriptionManager.setAgentSubscription({} as AgentSubscription);
  }

  return {
    resetAgentWorkflow,
    resetAgentSubscription,
    clearAgentWorkflow,
    clearAgentSubscription,
  }

}

/** 更新 agent workflow 数据 */
const useUpdateAgentWorkflow = () => {
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const { workflowError, workflowAddressError, validateMap, validateAll, resetError } = useValidateAgentWorkflow();

  const updateWorkflow = async () => {
    setLoading(true);
    // 调接口前的兜底校验
    const valid = validateAll(agentWorkflowSubscriptionManager.getAgentWorkflow());
    if (!valid) {
      setLoading(false);
      return false;
    }
    try {
      await agentWorkflowSubscriptionManager.updateWorkflow();
      await agentWorkflowSubscriptionManager.fetchWorkflow();
    } catch (error) {
      toast.showToastByCode(error);
    } finally {
      setLoading(false);
    }
  }

  const onValueChange = (key: keyof AgentSetWorkflow, value: string) => {
    // 输入即校验数据（要保证 key 与 validateMap 中的 key 一致）
    validateMap[key](value);

    agentWorkflowSubscriptionManager.setAgentWorkflow({
      ...agentWorkflowSubscriptionManager.getAgentWorkflow(),
      [key]: value,
    });
  }

  return {
    loading,
    updateWorkflow,

    workflowError,
    workflowAddressError,
    onValueChange,
    resetError,
  }
}

/** 更新 agent subscription 数据 */
const useUpdateAgentSubscription = () => {
  const toast = useToast();
  const [loading, setLoading] = useState(false);

  const updateSubscription = async () => {
    setLoading(true);
    try {
      await agentWorkflowSubscriptionManager.updateSubscription();
      await agentWorkflowSubscriptionManager.fetchWorkflow();
    } catch (error) {
      toast.showToastByCode(error);
    } finally {
      setLoading(false);
    }
  }

  const onValueChange = (key: keyof AgentSubscription, value: number) => {
    agentWorkflowSubscriptionManager.setAgentSubscription({
      ...agentWorkflowSubscriptionManager.getAgentSubscription(),
      [key]: value,
    });
  }

  return {
    loading,
    updateSubscription,
    onValueChange,
  }
}

/** 校验 agent workflow 数据 */
const useValidateAgentWorkflow = () => {
  const { t } = useTranslation();
  const [workflowError, setWorkflowError] = useState('');
  const [workflowAddressError, setWorkflowAddressError] = useState('');

  // 校验 workflow 数据
  const validateWorkflow = (value: string) => {
    if (!value) {
      setWorkflowError(t('agent_dashboard.workflow.workflow_Name_placeholder'));
      return false;
    } else {
      setWorkflowError('');
      return true;
    }
  }

  // 校验 workflowAddress 数据
  const validateWorkflowAddress = (value: string) => {
    if (!value) {
      setWorkflowAddressError(t('agent_dashboard.workflow.workflow_address_placeholder'));
      return false;
    } else {
      setWorkflowAddressError('');
      return true;
    }
  }

  // 校验所有数据
  const validateAll = (formData: AgentSetWorkflow) => {
    return [
      validateWorkflow(formData.workflow),
      validateWorkflowAddress(formData.workflowAddress),
    ].every(Boolean);
  }

  // 重置校验错误
  const resetError = () => {
    setWorkflowError('');
    setWorkflowAddressError('');
  }

  const validateMap = {
    workflow: validateWorkflow,
    workflowAddress: validateWorkflowAddress,
  }

  return {
    workflowError,
    workflowAddressError,

    validateMap,
    validateAll,
    resetError,
  }
}

/** 深度对比 agent workflow 数据是否有变化 */
const useAgentWorkflowChanged = () => {
  return {
    isChanged: !deepEqual<AgentSetWorkflow>(agentWorkflowSubscriptionManager.getAgentWorkflow(), agentWorkflowSubscriptionManager.getOriginAgentWorkflow()),
  }
}

/** 深度对比 agent subscription 数据是否有变化 */
const useAgentSubscriptionChanged = () => {
  return {
    isChanged: !deepEqual<AgentSubscription>(agentWorkflowSubscriptionManager.getAgentSubscription(), agentWorkflowSubscriptionManager.getOriginAgentSubscription()),
  }
}

export const agentInfoHooks = {
  useAgentWorkflowSubscriptionData,
  useUpdateAgentWorkflow,
  useUpdateAgentSubscription,
  useAgentWorkflowChanged,
  useAgentSubscriptionChanged,
  useAgentAction,
  useValidateAgentWorkflow,
  useListenerBeforeAction,
  useListenerAfterAction,
}

// 导出单例实例
export const agentWorkflowSubscriptionManager = AgentWorkflowSubscriptionManager.getInstance();
